import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShieldCheck, MapPin, Sparkles, ArrowLeft, IndianRupee, Lock, Gift } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";
import { memberApi } from "@/lib/memberClient";
import { getGuestToken } from "@/lib/guestToken";
import { useMember } from "@/contexts/MemberContext";
import { BRAND } from "@/constants/brand";

const QUICK_AMOUNTS = [30, 50, 80, 100, 150];

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function PayScratchPay() {
  const { outletCode = "main" } = useParams();
  const nav = useNavigate();
  const { member } = useMember();
  const [outlet, setOutlet] = useState(null);
  const [config, setConfig] = useState(null);
  const [amount, setAmount] = useState("");
  const [customerName, setCustomerName] = useState(member?.name || "");
  const [customerPhone, setCustomerPhone] = useState(member?.phone || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/pay-scratch/config").then(({ data }) => {
      setConfig(data.config);
      const found = (data.outlets || []).find((o) => o.code === outletCode.toLowerCase());
      setOutlet(found || null);
    }).catch(() => setErr("Unable to load Pay & Scratch"));
  }, [outletCode]);

  useEffect(() => {
    if (member) {
      setCustomerName(member.name || "");
      setCustomerPhone(member.phone || "");
    }
  }, [member]);

  const proceed = async () => {
    setErr(null);
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    if (amt > 100000) return toast.error("Amount too large");
    if (!outlet) return toast.error("Outlet unavailable");
    if (!config?.is_enabled) return toast.error("Pay & Scratch is currently unavailable");

    setBusy(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Payment SDK failed to load. Check your connection.");

      const client = member ? memberApi : api;
      const { data: order } = await client.post("/pay-scratch/order", {
        outlet_code: outlet.code,
        amount: amt,
        guest_token: member ? undefined : getGuestToken(),
        customer_name: customerName || undefined,
        customer_phone: customerPhone || undefined,
      });

      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Pop Panda",
        description: `Payment at ${order.outlet.name}`,
        image: BRAND.logoUrl,
        order_id: order.order_id,
        prefill: {
          name: order.customer.name || undefined,
          email: order.customer.email || undefined,
          contact: order.customer.contact || undefined,
        },
        notes: { outlet: order.outlet.name },
        theme: { color: "#FFC93C" },
        handler: async (resp) => {
          try {
            const verifyClient = member ? memberApi : api;
            const { data: v } = await verifyClient.post("/pay-scratch/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              guest_token: member ? undefined : getGuestToken(),
            });
            nav(`/scratch/card/${v.card_token}?p=${v.payment_id}`, { replace: true });
          } catch (e) {
            toast.error(formatApiError(e));
          }
        },
        modal: {
          ondismiss: () => setBusy(false),
        },
      });
      rzp.on("payment.failed", (r) => {
        toast.error(r?.error?.description || "Payment failed. Try again.");
        setBusy(false);
      });
      rzp.open();
    } catch (e) {
      setErr(formatApiError(e));
      setBusy(false);
    }
  };

  if (err) {
    return (
      <div className="min-h-screen bg-[#0a0714] text-brand-cream grid place-items-center px-6 text-center">
        <div>
          <div className="text-brand-yellow uppercase tracking-[0.28em] text-xs mb-2">Pay & Scratch</div>
          <div className="font-display text-3xl">{err}</div>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 text-sm text-brand-cream/70 hover:text-brand-cream"><ArrowLeft size={14} /> Back home</Link>
        </div>
      </div>
    );
  }
  if (!outlet || !config) {
    return <div className="min-h-screen bg-[#0a0714] text-brand-cream grid place-items-center">Loading…</div>;
  }

  return (
    <div
      data-testid="pay-scratch-page"
      className="min-h-screen text-brand-cream relative overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse at 20% 10%, #1e0d3e 0%, #0e0a24 40%, #0a0714 80%, #050308 100%)",
      }}
    >
      {/* Ambient bg */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div className="absolute -top-40 -left-32 w-[520px] h-[520px] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(255,201,60,0.24), transparent 60%)" }} animate={{ x: [0, 30, 0], y: [0, 20, 0] }} transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }} />
        <motion.div className="absolute bottom-0 -right-32 w-[440px] h-[440px] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(139,92,246,0.24), transparent 60%)" }} animate={{ x: [0, -30, 0], y: [0, -20, 0] }} transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }} />
      </div>

      {/* Top bar */}
      <header className="relative z-10 flex items-center justify-between px-5 py-5 max-w-lg mx-auto">
        <Link to="/" className="flex items-center gap-2" data-testid="pay-back">
          <ArrowLeft size={16} className="text-brand-cream/70" />
          <img src={BRAND.logoUrl} alt="Pop Panda" className="h-8 w-8 bg-brand-cream rounded-full p-1" />
          <div className="font-display text-lg">Pop Panda</div>
        </Link>
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-emerald-400 bg-emerald-500/10 border border-emerald-400/25 rounded-full px-2.5 py-1">
          <ShieldCheck size={11} /> 100% Secure
        </div>
      </header>

      {/* Main card */}
      <main className="relative z-10 max-w-lg mx-auto px-5 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <div className="text-[10px] uppercase tracking-[0.32em] text-brand-yellow inline-flex items-center gap-1.5"><Sparkles size={11} /> {config.header_title}</div>
          <h1 className="font-display text-3xl mt-2">Pay & Win Instantly</h1>
          <p className="text-brand-cream/70 text-sm mt-1.5">{config.header_subtitle}</p>
        </motion.div>

        {/* Outlet card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="mt-6 rounded-3xl bg-white/[0.05] border border-white/12 backdrop-blur-2xl p-5 shadow-xl"
          data-testid="pay-outlet-card"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-brand-cream/60 inline-flex items-center gap-1.5"><MapPin size={11} /> Paying at</div>
              <div className="font-display text-xl mt-0.5" data-testid="pay-outlet-name">{outlet.name}</div>
              {outlet.address && <div className="text-xs text-brand-cream/50 mt-0.5">{outlet.address}</div>}
            </div>
            <div className="relative">
              <div className="w-12 h-12 rounded-full grid place-items-center" style={{ background: "radial-gradient(circle at 30% 30%, #fff1b3, #FFC93C 55%, #E5A100 100%)" }}>
                <Gift size={22} className="text-[#3a2200]" strokeWidth={2.4} />
              </div>
              <motion.div className="absolute inset-0 rounded-full pointer-events-none" animate={{ boxShadow: ["0 0 0 0 rgba(255,201,60,0.55)", "0 0 0 14px rgba(255,201,60,0)"] }} transition={{ duration: 1.8, repeat: Infinity }} />
            </div>
          </div>
        </motion.div>

        {/* Amount card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="mt-5 rounded-3xl bg-white/[0.06] border border-white/15 backdrop-blur-2xl p-6 shadow-2xl"
        >
          <label className="text-[10px] uppercase tracking-widest text-brand-cream/60">Bill amount</label>
          <div className="mt-2 flex items-center gap-2 rounded-2xl bg-black/40 border border-white/15 px-4 py-4">
            <IndianRupee size={22} className="text-brand-yellow" />
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="1"
              max="100000"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="pay-amount-input"
              placeholder="0.00"
              className="flex-1 bg-transparent outline-none text-brand-cream font-display text-3xl"
            />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {QUICK_AMOUNTS.map((q) => (
              <button
                key={q}
                onClick={() => setAmount(String(q))}
                data-testid={`pay-quick-${q}`}
                className="text-xs rounded-full bg-white/8 hover:bg-white/16 border border-white/20 text-brand-cream/80 px-3 py-1.5 transition"
              >
                ₹{q}
              </button>
            ))}
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-brand-cream/60">Name (optional)</label>
              <input value={customerName} onChange={(e) => setCustomerName(e.target.value)} data-testid="pay-name-input" className="mt-1 w-full rounded-xl bg-black/40 border border-white/15 px-3 py-2.5 text-sm outline-none focus:border-brand-yellow" placeholder="Your name" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-brand-cream/60">Phone (optional)</label>
              <input value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} data-testid="pay-phone-input" className="mt-1 w-full rounded-xl bg-black/40 border border-white/15 px-3 py-2.5 text-sm outline-none focus:border-brand-yellow" placeholder="10-digit number" />
            </div>
          </div>

          <button
            onClick={proceed}
            disabled={busy}
            data-testid="pay-proceed-btn"
            className="mt-6 w-full rounded-full px-6 py-4 text-base font-semibold text-[#1a0f2f] disabled:opacity-60 inline-flex items-center justify-center gap-2 transition-all"
            style={{
              background: "linear-gradient(135deg, #FFE07A 0%, #FFC93C 50%, #E5A100 100%)",
              boxShadow: "0 12px 32px rgba(255,201,60,0.4), inset 0 1px 0 rgba(255,255,255,0.4)",
            }}
          >
            <Lock size={16} /> {busy ? "Opening secure checkout…" : `Proceed to Pay ${amount ? `₹${parseFloat(amount).toFixed(2)}` : ""}`}
          </button>

          <div className="mt-4 flex items-center justify-between text-[10px] uppercase tracking-widest text-brand-cream/50">
            <div className="inline-flex items-center gap-1.5"><ShieldCheck size={11} /> Razorpay Secured</div>
            <div>UPI · Cards · Netbanking</div>
          </div>
        </motion.div>

        {/* Perks */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="mt-5 rounded-2xl bg-brand-yellow/[0.08] border border-brand-yellow/30 backdrop-blur-xl p-4"
        >
          <div className="text-[10px] uppercase tracking-[0.28em] text-brand-yellow inline-flex items-center gap-1.5"><Sparkles size={11} /> Every payment wins</div>
          <div className="mt-1 text-sm text-brand-cream/85">
            One successful payment = one <span className="text-brand-yellow font-semibold">Instant Scratch Card</span>. Reveal Pop Coins, free popsicles, mystery rewards & more.
          </div>
        </motion.div>

        {member ? (
          <div className="mt-4 text-center text-xs text-brand-cream/50">Signed in as <span className="text-brand-cream">{member.name || member.email}</span></div>
        ) : (
          <div className="mt-4 text-center text-xs text-brand-cream/50">
            You&apos;ll be able to save your reward to your Panda Club after scratching.
          </div>
        )}
      </main>
    </div>
  );
}
